import clsx from 'clsx'
import { ComponentPropsWithoutRef, forwardRef, useEffect, useState } from 'react'

export type AvatarProfilePhotoSize = 'sm' | 'md' | 'lg'

export interface AvatarProfilePhotoProps extends Omit<
    ComponentPropsWithoutRef<'span'>,
    'children'
> {
    size?: AvatarProfilePhotoSize
    src?: string
    alt?: string
    initials?: string
    verified?: boolean
}

const SIZE_CLASSES: Record<AvatarProfilePhotoSize, string> = {
    sm: 'size-[72px]',
    md: 'size-24',
    lg: 'size-40',
}

const PHOTO_OUTLINE_CLASSES: Record<AvatarProfilePhotoSize, string> = {
    sm: 'border-[0.5px]',
    md: 'border-[0.75px]',
    lg: 'border-[0.75px]',
}

const PLACEHOLDER_ICON_CLASSES: Record<AvatarProfilePhotoSize, string> = {
    sm: 'size-9',
    md: 'size-12',
    lg: 'size-[72px]',
}

const INITIALS_CLASSES: Record<AvatarProfilePhotoSize, string> = {
    sm: 'text-display-xs',
    md: 'text-display-sm',
    lg: 'text-display-lg',
}

const SEAL_CLASSES: Record<AvatarProfilePhotoSize, string> = {
    sm: 'size-5',
    md: 'size-6',
    lg: 'size-8',
}

function VerifiedSeal({ size }: { size: AvatarProfilePhotoSize }) {
    return (
        <svg
            viewBox="0 0 32 32"
            fill="none"
            className={clsx('text-brand-500', SEAL_CLASSES[size])}
            aria-hidden="true">
            <path
                fill="currentColor"
                d="M24.7108 5.66727C25.0148 6.4023 25.5981 6.98655 26.3327 7.29157L28.9085 8.3585C29.6436 8.66298 30.2276 9.247 30.5321 9.98208C30.8366 10.7172 30.8366 11.5431 30.5321 12.2782L29.4659 14.8521C29.1613 15.5875 29.1609 16.4143 29.4669 17.1493L30.5312 19.7225C30.6821 20.0866 30.7598 20.4768 30.7599 20.871C30.76 21.2651 30.6824 21.6554 30.5316 22.0195C30.3807 22.3836 30.1596 22.7145 29.8809 22.9931C29.6022 23.2718 29.2713 23.4927 28.9071 23.6435L26.3331 24.7096C25.5981 25.0135 25.0139 25.5969 24.7088 26.3315L23.6419 28.9073C23.3374 29.6423 22.7534 30.2264 22.0183 30.5308C21.2832 30.8353 20.4573 30.8353 19.7222 30.5308L17.1483 29.4647C16.4132 29.161 15.5876 29.1616 14.8529 29.4664L12.2771 30.5318C11.5425 30.8356 10.7173 30.8353 9.98276 30.5311C9.24828 30.2269 8.66458 29.6435 8.35989 28.9092L7.29265 26.3327C6.98873 25.5977 6.40536 25.0134 5.67079 24.7084L3.09501 23.6415C2.36025 23.3371 1.7764 22.7535 1.47181 22.0188C1.16721 21.2841 1.1668 20.4586 1.47066 19.7236L2.53681 17.1497C2.84055 16.4146 2.83993 15.5889 2.53509 14.8543L1.47046 12.2767C1.31952 11.9126 1.2418 11.5223 1.24174 11.1282C1.24167 10.7341 1.31927 10.3438 1.4701 9.97969C1.62092 9.61556 1.84202 9.28472 2.12075 9.00608C2.39949 8.72743 2.7304 8.50644 3.09457 8.35573L5.6685 7.28957C6.40289 6.98592 6.98679 6.40328 7.29204 5.66956L8.35896 3.09378C8.66344 2.3587 9.24747 1.77467 9.98255 1.47019C10.7176 1.16571 11.5436 1.16571 12.2787 1.47019L14.8526 2.53635C15.5877 2.84008 16.4133 2.83946 17.148 2.53462L19.7248 1.47185C20.4598 1.16753 21.2856 1.1676 22.0205 1.47202C22.7554 1.77644 23.3394 2.3603 23.6439 3.09519L24.7112 5.67173L24.7108 5.66727Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                fill="white"
                d="M22.2665 11.8058C22.4803 11.4701 22.5519 11.0631 22.4656 10.6746C22.3794 10.286 22.1423 9.94759 21.8065 9.73383C21.4708 9.52006 21.0638 9.44843 20.6753 9.5347C20.2867 9.62097 19.9483 9.85806 19.7345 10.1938L13.8605 19.4238L11.1725 16.0638C10.924 15.753 10.5622 15.5536 10.1667 15.5095C9.77118 15.4655 9.37436 15.5803 9.06352 15.8288C8.75269 16.0773 8.55331 16.4391 8.50923 16.8347C8.46516 17.2302 8.58001 17.627 8.82852 17.9378L12.8285 22.9378C12.9776 23.1244 13.1691 23.2727 13.387 23.3703C13.6049 23.468 13.843 23.5122 14.0815 23.4993C14.32 23.4864 14.5519 23.4167 14.758 23.2961C14.9641 23.1755 15.1385 23.0074 15.2665 22.8058L22.2665 11.8058Z"
            />
        </svg>
    )
}

function UserIcon({ size }: { size: AvatarProfilePhotoSize }) {
    return (
        <svg
            className={clsx('text-foreground-fg-quaternary', PLACEHOLDER_ICON_CLASSES[size])}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true">
            <path d="M20 21C20 19.6044 20 18.9067 19.8278 18.3389C19.44 17.0605 18.4395 16.06 17.1611 15.6722C16.5933 15.5 15.8956 15.5 14.5 15.5H9.5C8.10444 15.5 7.40665 15.5 6.83886 15.6722C5.56045 16.06 4.56004 17.0605 4.17224 18.3389C4 18.9067 4 19.6044 4 21M16.5 7.5C16.5 9.98528 14.4853 12 12 12C9.51472 12 7.5 9.98528 7.5 7.5C7.5 5.01472 9.51472 3 12 3C14.4853 3 16.5 5.01472 16.5 7.5Z" />
        </svg>
    )
}

export const AvatarProfilePhoto = forwardRef<HTMLSpanElement, AvatarProfilePhotoProps>(
    ({ size = 'md', src, alt = '', initials, verified = false, className, ...rest }, ref) => {
        const [imageError, setImageError] = useState(false)

        useEffect(() => {
            setImageError(false)
        }, [src])

        const hasImage = Boolean(src) && !imageError
        const hasInitials = !hasImage && Boolean(initials)

        const accessibleName = alt || initials || 'Profile photo'
        const verifiedLabel = verified ? ', verified' : ''

        return (
            <span
                ref={ref}
                role="img"
                aria-label={`${accessibleName}${verifiedLabel}`}
                className={clsx(
                    'relative inline-flex shrink-0 rounded-radius-sm bg-background-bg-primary',
                    SIZE_CLASSES[size],
                    className,
                )}
                {...rest}>
                {hasImage ? (
                    <span className="relative flex size-full overflow-hidden rounded-radius-sm bg-background-bg-primary">
                        <img
                            src={src}
                            alt=""
                            className="absolute inset-0 size-full object-cover"
                            onError={() => setImageError(true)}
                        />
                        <span
                            aria-hidden="true"
                            className={clsx(
                                'pointer-events-none absolute inset-0 rounded-radius-sm border-solid border-base-black/[0.16]',
                                PHOTO_OUTLINE_CLASSES[size],
                            )}
                        />
                    </span>
                ) : (
                    <span className="flex size-full items-center justify-center overflow-hidden rounded-radius-sm bg-background-bg-tertiary ring-1 ring-alpha-black-10">
                        {hasInitials ? (
                            <span
                                aria-hidden="true"
                                className={clsx(
                                    'font-display font-semibold text-text-quaternary',
                                    INITIALS_CLASSES[size],
                                )}>
                                {initials}
                            </span>
                        ) : (
                            <UserIcon size={size} />
                        )}
                    </span>
                )}

                {verified && (
                    <span className="absolute -bottom-1 -right-1">
                        <VerifiedSeal size={size} />
                    </span>
                )}
            </span>
        )
    },
)

AvatarProfilePhoto.displayName = 'AvatarProfilePhoto'
